import axios from 'axios';
import AppConstants from '../AppConstants';
import { getAccessToken } from '../Authentication/AuthenticationService';
import { MAX_RANGE, MIN_RANGE, RecordingSource, ReportType } from '../Utils/Constants';
import moment from 'moment-timezone';
import { isArrayEqual } from '../Utils/ArrayUtils';

const baseUrl = AppConstants.REPORTING_READ_BASE_URL;
const virtualEventsBaseUrl = AppConstants.REPORTING_VIRTUAL_EVENT_READ_BASE_URL;
const getVirtualEventsBaseUrl = AppConstants.GET_VIRTUAL_EVENTS_LIST_URL;
const fetchSize = AppConstants.SESSIONS_DATA_FETCH_SIZE;

export function getWebinars(fromTime, toTime) {
  return axios.get(baseUrl + '/api/webinars', {
    params: {
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      includes: 'registrationstatistics,havingregistrations',
      client: AppConstants.APP_NAME
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getSessions(fromTime, toTime, pageNum = 0, limit = fetchSize) {
  return axios.get(baseUrl + '/api/V2/sessions', {
    params: {
      page: pageNum,
      limit: limit,
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      client: AppConstants.APP_NAME,
      includes: 'sessionstats'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getRecordings(fromTime, toTime) {
  return axios.get(baseUrl + '/api/recordings', {
    params: {
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      includes: 'viewscount',
      client: AppConstants.APP_NAME
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getRecordingDetails(recordingKey, withViews = true) {
  let params = { client: AppConstants.APP_NAME };

  if (withViews) {
    params['includes'] = 'registrants';
  }
  return axios.get(`${baseUrl}/api/recordings/${recordingKey}`, {
    params,
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getRecordingViewers(recordingKey, source = RecordingSource.G2W) {
  let params = { client: AppConstants.APP_NAME };
  switch (source) {
    case RecordingSource.G2W:
      params.source = RecordingSource.G2W;
      break;
    case RecordingSource.G2S:
      params.source = 'G2S-FRONTEND';
      break;
    case RecordingSource.G2S_EMBEDDED:
      params.source = 'G2S-EMBEDDED';
      break;
    default:
      console.warn('ignoring invalid source');
  }
  return axios.get(`${baseUrl}/api/v3/recordings/${recordingKey}/views`, {
    params,
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

function postReportGenerationJob(inputObject, reportType, sendEmail = false, multipleReportType) {
  if (
    reportType === ReportType.VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT ||
    reportType === ReportType.VIRTUAL_EVENT_REGISTRATION_REPORT
  ) {
    return axios.post(
      virtualEventsBaseUrl + `/v1/jobs?sendEmail=${sendEmail}&client=${AppConstants.APP_NAME}`,
      {
        reportType: multipleReportType || reportType,
        productType: 'G2W',
        input: JSON.stringify(inputObject)
      },
      {
        headers: {
          Authorization: 'Bearer ' + getAccessToken()
        }
      }
    );
  } else {
    return axios.post(
      baseUrl + `/jobs?sendEmail=${sendEmail}&client=${AppConstants.APP_NAME}`,
      {
        type: reportType,
        input: JSON.stringify(inputObject)
      },
      {
        headers: {
          Authorization: 'Bearer ' + getAccessToken()
        }
      }
    );
  }
}

export function submitReportJob(reportType, reportFormatType, keys, user, sendEmail = false) {
  const { userTimeZone: timeZone, userLocale: locale } = user;
  let input = null;
  let multipleReportType;
  if (
    reportType === ReportType.VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT ||
    reportType === ReportType.VIRTUAL_EVENT_REGISTRATION_REPORT
  ) {
    input = { timezone: timeZone, locale, product: 'G2W', format: reportFormatType.toUpperCase() };
  } else {
    input = { timeZone, locale, product: 'G2W', format: reportFormatType };
  }
  let keyObject = null;
  switch (reportType) {
    case ReportType.SESSIONS_QANDA_FOLLOW_UP_REPORT:
    case ReportType.SESSIONS_ATTENDANCE_REPORT:
    case ReportType.SESSIONS_OVERVIEW_REPORT:
      keyObject = { sessionKeys: keys };
      break;
    case ReportType.WEBINAR_ATTENDEE_REPORT:
    case ReportType.WEBINAR_PERFORMANCE_REPORT:
    case ReportType.WEBINAR_SURVEY_REPORT:
    case ReportType.WEBINAR_QANDA_REPORT:
      keyObject = { sessionKey: keys[0] };
      break;
    case ReportType.WEBINAR_REGISTRATION_REPORT:
      keyObject = { webinarKey: keys[0] };
      break;
    case ReportType.WEBINAR_RECORDING_REPORT:
      keyObject = { recordingKey: keys[0] };
      break;
    case ReportType.VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT:
      if (keys.length === 1) {
        keyObject = { virtualEventKey: keys[0], reportType: reportType };
      } else if (keys.length > 1) {
        keyObject = { virtualEventKeys: keys, reportType: reportType };
        multipleReportType = ReportType.MULTIPLE_VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT;
      }
      break;
    case ReportType.VIRTUAL_EVENT_REGISTRATION_REPORT:
      if (keys.length === 1) {
        keyObject = { virtualEventKey: keys[0], reportType: reportType };
      } else if (keys.length > 1) {
        keyObject = { virtualEventKeys: keys, reportType: reportType };
        multipleReportType = ReportType.MULTIPLE_VIRTUAL_EVENT_REGISTRATION_REPORT;
      }
      break;
    default:
      console.error('invalid reportType');
      break;
  }
  return postReportGenerationJob({ ...input, ...keyObject }, reportType, sendEmail, multipleReportType);
}

export function checkJobStatus(JobId, sendEvent, reportType) {
  if (
    reportType === ReportType.VIRTUAL_EVENT_REGISTRATION_REPORT ||
    reportType === ReportType.VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT
  ) {
    return axios.get(virtualEventsBaseUrl + '/v1/jobs/' + JobId, {
      params: { sendEvent: sendEvent },
      headers: { Authorization: 'Bearer ' + getAccessToken() }
    });
  }
  return axios.get(baseUrl + '/jobs/' + JobId, {
    params: { sendEvent: sendEvent },
    headers: { Authorization: 'Bearer ' + getAccessToken() }
  });
}

export function getSessionDetails(sessionKey) {
  return axios.get(`${baseUrl}/api/V2/sessions/${sessionKey}`, {
    params: {
      client: AppConstants.APP_NAME,
      includes: 'sessionstats'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getSessionPolls(webinarKey, sessionKey) {
  return axios.get(`${baseUrl}/api/webinars/${webinarKey}/sessions/${sessionKey}/polls`, {
    params: {
      client: AppConstants.APP_NAME
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getSessionSurveys(webinarKey, sessionKey) {
  return axios.get(`${baseUrl}/api/webinars/${webinarKey}/sessions/${sessionKey}/surveys`, {
    params: {
      client: AppConstants.APP_NAME,
      includes: 'ANSWEREREMAIL'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getVirtualEvents(fromTime, toTime) {
  return axios.post(`${getVirtualEventsBaseUrl}/events/list`, [], {
    params: {
      timeRangeFrom: moment(fromTime).format(),
      timeRangeTo: moment(toTime).format(),
      client: AppConstants.APP_NAME
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getAttendees(sessionKey, pageNumber, filter, sort) {
  return getSessionRegistrantDetails(sessionKey, pageNumber, filter, sort, true);
}
export function getSessionRegistrantDetails(sessionKey, pageNumber, filter, sort, attended = null) {
  return axios.get(
    `${baseUrl}/api/v3/sessions/${sessionKey}/sessionregistrants?&textSearch=${encodeURIComponent(filter.textSearch)}`,
    {
      params: {
        client: AppConstants.APP_NAME,
        page: pageNumber,
        size: 50,
        attended: attended,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder
      },
      headers: {
        Authorization: 'Bearer ' + getAccessToken()
      }
    }
  );
}

export function getSessionQuestions(sessionKey, page, size, textSearch, answered, dateAskedSortOrder) {
  return axios.get(`${baseUrl}/api/v3/sessions/${sessionKey}/questions?&textSearch=${encodeURIComponent(textSearch)}`, {
    params: {
      page,
      size,
      client: AppConstants.APP_NAME,
      showAnswered: answered,
      sortBy: 'DATE_ASKED',
      sortOrder: dateAskedSortOrder
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getAttendeeDetailsPerSession(webinarKey, sessionKey) {
  return axios.get(`${baseUrl}/api/webinars/${webinarKey}/sessions/${sessionKey}/performance`, {
    params: {
      client: AppConstants.APP_NAME,
      includes: 'attendanceprofile'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getTrendComparisonData(fromTime, toTime) {
  let body = {
    fromTime: fromTime,
    toTime: toTime
  };

  return axios.post(baseUrl + '/api/v1/statistics/organizerusage', body, {
    params: {
      client: AppConstants.APP_NAME
    },
    headers: { Authorization: 'Bearer ' + getAccessToken() }
  });
}

export function getAdminSessions(
  accountKey,
  fromTime,
  toTime,
  page = 0,
  limit = fetchSize,
  filter,
  creators,
  registrantRange,
  attendeeRange
) {
  const params = {
    page,
    limit,
    fromTime: moment(fromTime).format(),
    toTime: moment(toTime).format(),
    client: AppConstants.APP_NAME,
    includes: 'sessionstats,creator'
  };
  if (filter) {
    params.filter = `title=${filter}`;
  }
  if (creators) {
    params.creators = creators;
  }
  if (!isArrayEqual([MIN_RANGE, MAX_RANGE], registrantRange)) {
    params.minRegistrantCount = registrantRange[0];
    params.maxRegistrantCount = registrantRange[1];
  }
  if (!isArrayEqual([MIN_RANGE, MAX_RANGE], attendeeRange)) {
    params.minAttendeeCount = attendeeRange[0];
    params.maxAttendeeCount = attendeeRange[1];
  }
  return axios.get(`${baseUrl}/api/accounts/${accountKey}/sessions`, {
    params,
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getSessionCreators(accountKey, fromTime, toTime, page, filter, limit) {
  return axios.get(`${baseUrl}/api/accounts/${accountKey}/sessions/creators`, {
    params: {
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      page,
      limit,
      filter,
      sortOrder: 'ASC'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getAdminTrendComparisonData(accountKey, fromTime, toTime) {
  let body = {
    fromTime: fromTime,
    toTime: toTime
  };

  return axios.post(`${baseUrl}/api/v1/statistics/accounts/${accountKey}/accountusage`, body, {
    params: {
      client: AppConstants.APP_NAME
    },
    headers: { Authorization: 'Bearer ' + getAccessToken() }
  });
}

export function getWebinarCreators(accountKey, fromTime, toTime, page, filter, limit) {
  return axios.get(`${baseUrl}/api/accounts/${accountKey}/webinars/creators`, {
    params: {
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      page,
      limit,
      filter,
      sortOrder: 'ASC'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getAdminWebinars(accountKey, fromTime, toTime, page = 0, limit = fetchSize, filter, creators) {
  const params = {
    page,
    limit,
    fromTime: moment(fromTime).format(),
    toTime: moment(toTime).format(),
    includes: 'registrationstatistics,havingregistrations,creator,staff',
    client: AppConstants.APP_NAME
  };
  if (filter) {
    params.filter = `title=${filter}`;
  }
  if (creators) {
    params.creators = creators;
  }
  return axios.get(`${baseUrl}/api/v3/accounts/${accountKey}/webinars`, {
    params: params,
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getAdminRecordings(accountKey, fromTime, toTime, page = 0, limit = fetchSize, filter, creators) {
  const params = {
    page,
    limit,
    fromTime: moment(fromTime).format(),
    toTime: moment(toTime).format(),
    includes: 'viewscount'
  };
  if (filter) {
    params.filter = filter;
  }
  if (creators) {
    params.creators = creators;
  }
  return axios.get(`${baseUrl}/api/accounts/${accountKey}/recordings`, {
    params,
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}

export function getRecordingCreators(accountKey, fromTime, toTime, page, filter, limit) {
  return axios.get(`${baseUrl}/api/accounts/${accountKey}/recordings/creators`, {
    params: {
      fromTime: moment(fromTime).format(),
      toTime: moment(toTime).format(),
      page,
      limit,
      filter,
      sortOrder: 'ASC'
    },
    headers: {
      Authorization: 'Bearer ' + getAccessToken()
    }
  });
}



// WEBPACK FOOTER //
// ./src/Apis/ReportingReadApi.js